import { useEffect } from "react";
import { isArray, isEmpty, isObject } from "lodash";

const DEFAULT_OPTIONS = { wait: 0, cleanupCallback: null };

/**
 *
 * @param {Function} callback the useEffect callback to be called deferred
 * @param {Array} dependencies the array of dependecies to pass to useEffect
 * @param {{ wait: number?, cleanupCallback: Function? }} options The wait option allows
 *    allows deferring the effect call after the given amount of milliseconds. The
 *    cleanupCallback is called in the return function of the useEffect, and is called
 *    each time the dependencies change or the component is unmounted.
 */
// This is a custom hook that allows to run a useEffect callback after the javascript
// current processing queue has been run, by using setTimeout internally.
//
// It's useful for effects that need to access DOM properties, or wait for an event to
// have closed.
//
// Using the hook to be run after the processing queue (setTimeout delay set to 0) :
//
//   useDeferredEffect(() => doSomethingAsync(), [doSomethingAsync]);
//
// Using the hook to be run after 1 second (setTimeout delay set to 1000) :
//
//   useDeferredEffect(
//     () => doSomethingAfter1sec(),
//     [doSomethingAfter1sec],
//     { wait: 1000 }
//   );
//
// Using the hook without dependencies (run on each render) :
//
//   useDeferredEffect(() => doSomethingAsync());
//
// Using the hook without dependencies and after 1 second :
//
//   useDeferredEffect(() => doSomethingAsync(), { wait: 1000 });
//
// Providing a cleanup callback (usually the return value of useEffect) :
//
//   useDeferredEffect(
//     () => addEventListeners(),
//     [addEventListeners],
//     cleanupCallback: () => removeEventListeners()
//   );
//
// Note : thanks to ESLint custom configuration for "react-hooks/exhaustive-deps", the
//        dependencies array will automatically be validated by ESLint just like it would
//        for useEffect or any other hook with a dependencies array.
//
export const useDeferredEffect = (callback, dependencies, options = {}) => {
  // Allow passing the options argument without passing any dependencies
  if (!isArray(dependencies) && isObject(dependencies) && isEmpty(options)) {
    options = dependencies;
    dependencies = [];
  }

  // Fetch options, and ensure we get default options for properties that aren't set.
  const { wait, cleanupCallback } = { ...DEFAULT_OPTIONS, ...options };

  useEffect(() => {
    const timeoutId = setTimeout(callback, wait);

    return () => {
      // Ensure that the timeout is cleared if the depencies change or the component is
      // unmounted.
      if (timeoutId) clearTimeout(timeoutId);
      if (cleanupCallback) cleanupCallback();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};
